import React, { useState, useRef, useEffect } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import { InputBase, Paper, IconButton } from '@mui/material';
import LanguageSwitcher from './LanguageSwitcher';
import Search from './search';
import config from '../config';
import useTranslation from './useTranslation';


// Aggiungi translate="no" a tutte le icone esistenti
const fixIcons = () => {
  // Fix per le icone esistenti
  document.querySelectorAll('.material-symbols-outlined').forEach(icon => {
    icon.setAttribute('translate', 'no');
  });

  // Observer per le icone che vengono aggiunte dinamicamente
  const observer = new MutationObserver((mutations) => {
    mutations.forEach(mutation => {
      mutation.addedNodes.forEach(node => {
        if (node.classList?.contains('material-symbols-outlined')) {
          node.setAttribute('translate', 'no');
        }
        // Controlla anche i figli
        if (node.querySelectorAll) {
          node.querySelectorAll('.material-symbols-outlined').forEach(icon => {
            icon.setAttribute('translate', 'no');
          });
        }
      });
    });
  });

  // Osserva le modifiche al DOM
  observer.observe(document.body, {
    childList: true,
    subtree: true
  });
};

// Esegui appena possibile
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', fixIcons);
} else {
  fixIcons();
}

function Header({ logo }) {
  const { t } = useTranslation();  
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearchPanelOpen, setIsSearchPanelOpen] = useState(false);
  const [isSearchAnimating, setIsSearchAnimating] = useState(false);
  const searchInputRef = useRef(null);

  // Controlla se la ricerca è abilitata nel config
  const isSearchEnabled = config.components?.search?.enabled !== false;

  // Gestisce l'apertura del pannello di ricerca
  const triggerSearch = () => {
    const trimmedTerm = searchTerm.trim();
    
    if (trimmedTerm) {
      // Imposta lo stato di animazione
      setIsSearchAnimating(true);
      
      // Usa requestAnimationFrame per assicurare l'animazione
      requestAnimationFrame(() => {
        setIsSearchPanelOpen(true);
        
        // Rimuovi lo stato di animazione
        setTimeout(() => {
          setIsSearchAnimating(false);
        }, 300);
      });
    }
  };

  // Gestisce l'invio del form di ricerca
  const handleSearch = (e) => {
    e.preventDefault();
    triggerSearch();
  };

  // Chiude il pannello di ricerca
  const handleCloseSearchPanel = () => {
    // Imposta lo stato di animazione per la chiusura
    setIsSearchAnimating(true);
    
    // Chiudi il pannello dopo l'animazione
    setTimeout(() => {
      setIsSearchPanelOpen(false);
      setIsSearchAnimating(false);
      setSearchTerm('');
    }, 300);
  };

  // Gestisce la pressione dei tasti nell'input di ricerca
  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setSearchTerm('');
      searchInputRef.current?.blur();
    }
  };

  return (
    <>
      <header 
        className="d-flex align-items-center justify-content-between p-2 fixed-top" 
        style={{ 
          height: 'var(--height-header)',
          backgroundColor: 'var(--color-background-primary)',
          zIndex: 'var(--z-header)'
        }}
      >
        {/* Logo (spostato a sinistra) */}
        <div className="d-flex align-items-center">
          <img 
            src={logo} 
            alt="Nody Logo" 
            className="logo" 
            style={{
              filter: 'var(--logo-filter, none)' // Variabile per gestire l'inversione nel tema scuro
            }}
          />
        </div>

        <div className="d-flex align-items-center flex-grow-1 justify-content-center gap-3 mx-3">
          {/* Form di Ricerca (solo se abilitato) */}
          {isSearchEnabled && (
            <Paper
              component="form"
              onSubmit={handleSearch}
              className="search-form"
              sx={{
                border: 'var(--border-25)',
                borderRadius: 'var(--radius-lg)',
                flexGrow: 1,
                maxWidth: '500px',
                backgroundColor: 'var(--color-surface)'
              }}
            >
              <InputBase
                ref={searchInputRef}
                className="search-input font-titillium titillium-web-regular w-100"
                placeholder={t('cerca')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={handleKeyDown}
                sx={{
                  color: 'var(--color-text-secondary)',
                  fontSize: 'var(--font-size-base)',
                  padding: '0 10px'
                }}
              />
              <IconButton 
                type="submit" 
                className="search-button"
                onClick={(e) => {
                  e.preventDefault();
                  triggerSearch();
                }}
                sx={{
                  color: 'var(--color-text-muted)',
                  '&:hover': {
                    color: 'var(--color-text-secondary)'
                  }
                }}
                aria-label="search"
              >
                <SearchIcon />
              </IconButton>
            </Paper>
          )}
        </div>

        {/* Switcher Lingua (spostato a destra) */}
        <div className="d-flex align-items-center">
          <LanguageSwitcher />
        </div>
      </header>

      {/* Pannello di Ricerca (solo se abilitato) */}
      {isSearchEnabled && (
        <Search 
          isOpen={isSearchPanelOpen} 
          isAnimating={isSearchAnimating}
          onClose={handleCloseSearchPanel}
          initialSearchTerm={searchTerm}
        />
      )}
    </>
  );
}

export default Header;